import { FilterField, filterControlClass } from '@/components/admin/versioned/filter-field';
import {
  ALL_LEVELS_LABEL,
  PRESENTATION_FILTER_LABEL,
  RETIRED_LEVEL_LABEL,
} from '@/lib/persona-labels';
import { PERSONA_WORDING } from '@/lib/persona-wording';
import type {
  InvolvementLevel,
  PersonaFilters,
  PersonaPresentationFilter,
} from '@matter_extractor/shared';
import {
  PERSONA_LEVEL_FILTER_ALL,
  PERSONA_LEVEL_FILTER_RETIRED,
  personaPresentationFilterSchema,
} from '@matter_extractor/shared';
import { useId } from 'react';

/**
 * The persona's TWO own filters, at the head of « Plus de filtres » (refonte
 * modulaire §10, §15).
 *
 * The criterion puts its single « Exemples » select in this same slot, and
 * `filter-panel.tsx` was written naming this pair by hand: « the object's own
 * slot, first, because it is what somebody opened the panel for: the number of
 * examples for a criterion (§6), the presentation and the cran for a persona
 * (§10) ». §4 fixes the five ALWAYS-VISIBLE filters as the common ones, so an
 * object's own dimensions cannot be among them.
 *
 * The mockup draws them in the visible row instead, in place of « Protocole » -
 * a persona is chosen by an expérience and by no protocole (§10.2). That is a
 * form the layer cannot express without a second slot on `filter-bar.tsx`, and
 * §4's own sentence about the five is the fond. Noted in the MR rather than
 * silently resolved.
 *
 * ## The cran's options are ROWS, which is why the scale is a prop
 *
 * They are the crans of §11.5, which an operator renames, reorders and deletes.
 * No enum could be written once and stay true, so `personaLevelFilterSchema` is
 * a bare string and the two reserved values sit beside the identifiers: `all`,
 * the neutral state, and `retired`, which finds the personas whose cran has
 * left the scale. A cran identifier is a uuid, so neither word can collide.
 */

export interface PersonaFilterFieldsProps {
  filters: PersonaFilters;
  /** The scale as it stands, in order - the select's middle options. */
  levels: readonly InvolvementLevel[];
  onChange: (patch: Partial<PersonaFilters>) => void;
}

export function PersonaFilterFields({ filters, levels, onChange }: PersonaFilterFieldsProps) {
  const presentationId = useId();
  const levelId = useId();

  return (
    <>
      <FilterField label="Présentation" htmlFor={presentationId} className="w-40">
        <select
          id={presentationId}
          data-testid={`${PERSONA_WORDING.entity}-filtre-presentation`}
          value={filters.presentation}
          onChange={(event) =>
            onChange({ presentation: event.currentTarget.value as PersonaPresentationFilter })
          }
          className={filterControlClass(filters.presentation !== 'all', 'w-full')}
        >
          {personaPresentationFilterSchema.options.map((option) => (
            <option key={option} value={option}>
              {PRESENTATION_FILTER_LABEL[option]}
            </option>
          ))}
        </select>
      </FilterField>

      <FilterField label="Cran" htmlFor={levelId} className="w-45">
        <select
          id={levelId}
          data-testid={`${PERSONA_WORDING.entity}-filtre-cran`}
          value={filters.level}
          onChange={(event) => onChange({ level: event.currentTarget.value })}
          className={filterControlClass(filters.level !== PERSONA_LEVEL_FILTER_ALL, 'w-full')}
        >
          <option value={PERSONA_LEVEL_FILTER_ALL}>{ALL_LEVELS_LABEL}</option>
          {levels.map((level) => (
            <option key={level.id} value={level.id}>
              {level.name}
            </option>
          ))}
          {/*
            LAST, and after the scale rather than among it: the mockup detaches
            it with a separator, which a `<select>` has no way to draw. Its place
            at the foot says the same thing - it is not a cran, it is the
            question « which personas point at nothing any more ».
          */}
          <option value={PERSONA_LEVEL_FILTER_RETIRED}>{RETIRED_LEVEL_LABEL}</option>
        </select>
      </FilterField>
    </>
  );
}
